const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/**
 * Decodes HTML character references — numeric (`&#x20;`, `&#111;`) and a handful of common named
 * ones. The markdown serializer (BlockNote's `blocksToMarkdownLossy`) escapes spaces and letters
 * as numeric entities; without decoding they surface as literal `&#x20;` noise in stripped text.
 */
function decodeEntities(src: string): string {
  return src.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body[0] === '#') {
      const code =
        body[1] === 'x' || body[1] === 'X' ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
      return Number.isNaN(code) ? match : String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

type StripMarkdownOptions = {
  // Keep paragraph and line breaks (e.g. for exports) instead of collapsing everything onto one line.
  keepLineBreaks?: boolean;
};

export function stripMarkdown(src: string, { keepLineBreaks = false }: StripMarkdownOptions = {}): string {
  if (src.length === 0) return '';
  const stripped = src
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    // Link may itself contain a bracketed groupc `[[1]](url)` so allow one level of nested
    .replace(/!\[((?:[^[\]]|\[[^\]]*\])*)\]\([^)]*\)/g, '$1')
    .replace(/\[((?:[^[\]]|\[[^\]]*\])*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1')
    .replace(/_{1,2}([^_\n]+)_{1,2}/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\\([[\]()*_~])/g, '$1');
  // Decode entities last so escaped spaces/letters become real text, then collapse whitespace.
  const decoded = decodeEntities(stripped);
  if (!keepLineBreaks) return decoded.replace(/\s+/g, ' ').trim();
  return decoded
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
